import React from 'react';
import PropTypes from 'prop-types';
import { find, get, includes, isEmpty, last, map, omit } from 'lodash';
import SelectField from '../../SelectField';

const getValue = (value) => {
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return value.toJS().join(',');

  return value ? value.toString() : '';
};

class FormSelectField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: '',
    };
  }

  handleChange = (value) => {
    const { input, onAddNewOptionClick, onFilter, options } = this.props;
    const valueAux = `${value || ''}`;
    // Prevent empty value by selecting options with clearableValue equals false.
    const values = !isEmpty(valueAux)
      ? valueAux.split(',')
      : map([find(options, (item) => !item.clearableValue)], 'id');
    const anyIndex = values.indexOf('any');
    let newValue = values.toString();

    if (value === 'create-new' || last(values) === 'create-new') {
      onAddNewOptionClick();
    } else {
      if (anyIndex === 0) newValue = values.slice(1, values.length).toString();

      if (anyIndex === values.length - 1) newValue = 'any';

      input.onChange(newValue);

      if (onFilter) onFilter('', newValue);
    }
  };

  handleInputChange = (value, selectedValue) => {
    const { onFilter } = this.props;
    const { inputValue } = this.state;

    if (
      onFilter &&
      ((inputValue !== value && (value !== '' || (value === '' && !selectedValue))) ||
        (value === '' && inputValue !== ''))
    ) {
      onFilter(value, selectedValue);
    }

    this.setState({ inputValue: value });
  };

  render() {
    const {
      addNewOptionLabel,
      clearable,
      defaultOptionText,
      helperText,
      input: { value, onBlur },
      meta: { error, touched },
      multiple,
      onAddNewOptionClick,
      options,
      searchable,
      showAddNewOption,
      showDefaultOption,
      ...other
    } = this.props;
    const selectedValue = getValue(value);

    // filter options to always display add new option and default option
    const customFilterOptions = (candidate, input) => {
      if (input) {
        const itemName = get(candidate, 'label', get(candidate, 'name', ''));
        const itemValue = get(candidate, 'value', get(candidate, 'id', get(candidate, '_id', '')));

        return (
          itemValue === 'create-new' || itemValue === 'any' || includes(itemName.toLowerCase(), input.toLowerCase())
        );
      }

      return true;
    };

    if (showDefaultOption && !find(options, { id: 'any' })) {
      options.unshift({
        id: 'any',
        label: defaultOptionText,
        clearableValue: Boolean(clearable),
      });
    }

    if (showAddNewOption && !find(options, { id: 'create-new' })) {
      options.unshift({
        id: 'create-new',
        label: addNewOptionLabel,
      });
    }

    return (
      <SelectField
        clearable={clearable || false}
        error={touched && !!error}
        filterOption={showAddNewOption || showDefaultOption ? customFilterOptions : null}
        helperText={touched && !!error ? error : helperText}
        multiple={multiple}
        onBlur={() => onBlur(value)}
        onChange={this.handleChange}
        onInputChange={(inputValue) => this.handleInputChange(inputValue, selectedValue)}
        options={options}
        searchable={searchable}
        value={selectedValue}
        {...omit(other, 'onFilter')}
        // https://github.com/react-dnd/react-dnd/issues/701
        onDrop={(e) => e.preventDefault()}
      />
    );
  }
}

FormSelectField.propTypes = {
  addNewOptionLabel: PropTypes.string,
  clearable: PropTypes.bool,
  defaultOptionText: PropTypes.string,
  helperText: PropTypes.string,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  multiple: PropTypes.bool,
  onAddNewOptionClick: PropTypes.func,
  onFilter: PropTypes.func,
  options: PropTypes.array.isRequired,
  searchable: PropTypes.bool,
  showAddNewOption: PropTypes.bool,
  showDefaultOption: PropTypes.bool,
};

FormSelectField.defaultProps = {
  addNewOptionLabel: '',
  defaultOptionText: 'Any',
  multiple: false,
  searchable: true,
  showAddNewOption: false,
  showDefaultOption: false,
};

export default FormSelectField;
